'use client'

import {
  Coins,
  Dice5,
  Heart,
  LayoutDashboardIcon,
  MessageSquare,
  QrCode,
  SettingsIcon,
  ShieldCheckIcon,
  TableIcon,
  Tv,
  UsersIcon,
} from 'lucide-react'
import * as React from 'react'

import { NavMain } from '@/components/nav-main'
import { NavUser } from '@/components/nav-user'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'

type NavSubItem = {
  title: string
  url: string
  module?: string
}

type NavItem = {
  title: string
  url?: string
  icon: React.ReactNode
  module?: string | null
  superAdminOnly?: boolean
  items?: NavSubItem[]
}

type NavGroup = {
  label: string
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'ภาพรวม',
    items: [
      {
        title: 'แดชบอร์ด',
        url: '/admin',
        icon: <LayoutDashboardIcon />,
      },
    ],
  },
  {
    label: 'จัดการแขกและที่นั่ง',
    items: [
      {
        title: 'รายชื่อแขก',
        url: '/admin/guests',
        icon: <UsersIcon />,
        module: 'guests',
      },
      {
        title: 'จัดการโต๊ะ',
        url: '/admin/tables',
        icon: <TableIcon />,
        module: 'tables',
      },
      {
        title: 'ป้ายเช็กอิน (Signage)',
        url: '/signage/checkin',
        icon: <QrCode />,
      },
    ],
  },
  {
    label: 'กิจกรรมภายในงาน',
    items: [
      {
        title: 'คำอวยพร',
        url: '/admin/blessings',
        icon: <Heart />,
        module: 'blessings',
      },
      {
        title: 'รายการอั่งเปา',
        url: '/admin/angpao',
        icon: <Coins />,
        module: 'angpao',
      },
      {
        title: 'จอภาพของแขก',
        url: '/admin/wall',
        icon: <Tv />,
        module: 'wall',
      },
      {
        title: 'สุ่มหมายเลข',
        url: '/admin/random-number',
        icon: <Dice5 />,
        module: 'random-number',
      },
    ],
  },
  {
    label: 'ระบบและการตั้งค่า',
    items: [
      {
        title: 'จัดการ Rich Menu',
        url: '/admin/richmenu',
        icon: <MessageSquare />,
        module: 'richmenu',
      },
      {
        title: 'ผู้ดูแลระบบ',
        url: '/admin/users',
        icon: <ShieldCheckIcon />,
        superAdminOnly: true,
      },
      {
        title: 'ตั้งค่า',
        url: '/admin/settings',
        icon: <SettingsIcon />,
        module: 'settings',
      },
    ],
  },
]

type AppSidebarProps = React.ComponentProps<typeof Sidebar> & {
  allowedModules?: string[] | null
}

export function AppSidebar({ allowedModules, ...props }: AppSidebarProps) {
  const isSuperAdmin = allowedModules == null

  const filteredGroups = NAV_GROUPS.map((group) => {
    const filteredItems = group.items
      .filter((item) => {
        if (item.superAdminOnly) return isSuperAdmin
        if (!item.module && !item.items) return true
        if (item.module)
          return isSuperAdmin || (allowedModules ?? []).includes(item.module)
        return true
      })
      .map((item) => {
        if (!item.items) return item
        const filteredSubs = item.items.filter(
          (sub) =>
            !sub.module ||
            isSuperAdmin ||
            (allowedModules ?? []).includes(sub.module),
        )
        return { ...item, items: filteredSubs }
      })
      .filter((item) => !item.items || item.items.length > 0)

    return { ...group, items: filteredItems }
  }).filter((group) => group.items.length > 0)

  return (
    <Sidebar
      variant="inset"
      {...props}
    >
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              render={
                <a
                  href="#"
                  aria-label="OA KOI Wedding Dashboard"
                  onClick={(e) => e.preventDefault()}
                />
              }
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <LayoutDashboardIcon className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-lg leading-tight">
                <span className="truncate font-bold">OA KOI Wedding</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain groups={filteredGroups} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  )
}
